import { useEffect, useState } from "react";
import type { Appointment, WaitlistMatches } from "@dentacore/shared-types";

import { bookFromWaitlist, listWaitlistMatches } from "../api/waitlist";
import { clockAt } from "./grid-geometry";
import styles from "./waitlist-matches.module.css";
import { useBookingScoped } from "./booking-location";

/**
 * Who could fill the slot this booking just freed (STORY-038).
 *
 * ── When it appears ─────────────────────────────────────────────────────────
 *
 * Only when the selected booking has actually stopped holding its chair —
 * cancelled or no-show, the two states working plan §6.1 frees immediately.
 * Rendering it for a live booking would offer staff a queue for a slot that is
 * not free, which is how a helpful screen double-books somebody. The server
 * refuses that case too (`SLOT_NOT_FREE`); this just does not ask.
 *
 * ── What "matching" means, and what it does not ─────────────────────────────
 *
 * The server decides. It matches on **duration** and asks `ConflictService`
 * whether a booking of that length would actually be accepted in the freed
 * slot, so an entry listed here is one a booking would take.
 *
 * `preferredWindow` is free text by design — *"mornings"*, *"any Tuesday"* — so
 * it is **shown beside each name and never filtered on**. A machine parsing a
 * human's shorthand would get it wrong silently; a receptionist reading it
 * decides in a second.
 */
export function WaitlistMatchesStrip({
  appointment,
  timezone,
  onBooked,
}: {
  appointment: Appointment;
  /** The branch's own zone — the time the patient would actually arrive at. */
  timezone: string;
  onBooked: () => void;
}): React.JSX.Element | null {
  const here = useBookingScoped();
  const [matches, setMatches] = useState<WaitlistMatches | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Counts rather than toggles, so a second refusal re-runs the effect. */
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setMatches(null);
    setError(null);

    here(listWaitlistMatches)(appointment.id, controller.signal)
      .then(setMatches)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        // Not swallowed — an empty queue and a refused read look identical once
        // rendered, and the second is a fault worth seeing.
        setError(cause instanceof Error ? cause.message : "Could not read the waitlist.");
      });

    return () => controller.abort();
  }, [appointment.id, reload]);

  const take = (entryId: string): void => {
    if (matches === null) return;

    setBusy(true);
    setError(null);

    here(bookFromWaitlist)(entryId, { chairId: matches.chairId, startsAt: matches.startsAt })
      .then(() => onBooked())
      .catch((cause: unknown) => {
        // The server's own sentence. A refusal leaves them on the queue, so
        // re-reading shows who is still available for this slot.
        setError(cause instanceof Error ? cause.message : "That booking did not save.");
        setReload((count) => count + 1);
      })
      .finally(() => setBusy(false));
  };

  if (matches === null && error === null) {
    return (
      <p className={styles.reading} data-testid="waitlist-reading">
        Checking the waitlist…
      </p>
    );
  }

  return (
    <section className={styles.strip} data-testid="waitlist-matches">
      <div className={styles.head}>
        <span className={styles.label}>Could fill this slot</span>
        {matches !== null && (
          <span className={styles.when}>
            {clockAt(matches.startsAt, timezone)} · {String(matches.minutes)} min free
          </span>
        )}
      </div>

      {error !== null && (
        <p className={styles.error} data-testid="waitlist-error">
          {error}
        </p>
      )}

      {matches !== null && matches.entries.length === 0 && error === null && (
        <p className={styles.empty} data-testid="waitlist-empty">
          Nobody on the waitlist fits this slot.
        </p>
      )}

      {matches?.entries.map((entry) => (
        <div key={entry.id} className={styles.row} data-testid="waitlist-match">
          <div className={styles.who}>
            <span className={styles.name}>{entry.patientName}</span>
            <span className={styles.needs}>
              {String(entry.durationMinutes)} min
              {entry.procedureLabel === null ? "" : ` · ${entry.procedureLabel}`}
            </span>
          </div>

          {/*
            Free text, shown rather than matched on — the whole reason the
            column is not an enum. A receptionist reads "mornings" and decides;
            a machine parsing it would be guessing.
          */}
          {entry.preferredWindow !== null && (
            <span className={styles.window} data-testid="waitlist-window">
              Prefers {entry.preferredWindow}
            </span>
          )}

          <button
            type="button"
            className={styles.take}
            disabled={busy}
            onClick={() => take(entry.id)}
            data-testid="waitlist-book"
            data-entry={entry.id}
          >
            Offer this slot
          </button>
        </div>
      ))}
    </section>
  );
}
